import { formatNumber, formatDate as formatUiDate, useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';

import { getMondayOfWeek, todayIso } from '../../dispatch/weekModel.js';
import { usePayrollQuery } from '../../payroll/usePayrollQuery.js';

import { HOME_CARD_LINK, HomeCard, homeCardStatus } from './HomeCard.js';

/**
 * "Payroll this period" — what the current pay period has come to, and whether it is closed.
 *
 * ADMIN ONLY, AND THE GATE IS A ROLE. `GET /api/v1/payroll` is guarded by `requireApiAdmin`, not by
 * a permission key, so a Manager holding every financial override is still refused it. `HomeView`
 * therefore mounts this card behind `{ minRole: 'Admin' }` — the same rule `/payroll` carries in
 * `landing.ts` and in the sidebar — which is also what keeps the REQUEST from being made by
 * somebody guaranteed a 403.
 *
 * ⚠ EVERY FIGURE IS THE SERVER'S. `totalGrossPay` is the golden-locked payroll calc; nothing here
 * adds, rounds or re-derives a number that reaches a pay record (D-03). The card renders it and
 * links to the screen where it can be reviewed and committed.
 *
 * ⚠ THE WEEK IS SENT EXPLICITLY, and it has to be. `GET /api/v1/payroll` with no `week=` does NOT
 * answer with the current period — `resolvePayrollWeek` (api.ts:8931-8940) falls back to "this
 * week's Monday minus 7 days", the PREVIOUS full week, matching the Payroll Center's own default.
 * A card headed "Payroll this period" showing last week's gross would be wrong in the most
 * expensive place on the screen. `getMondayOfWeek` is the SPA's mirror of the server's Monday-start
 * week and reads the day in UTC, which is the trap that made a Monday print as Sunday west of UTC.
 *
 * The week is fixed to today's. A card that could point at another period would need a control to
 * change it, and this is not the screen for that — `/payroll` is.
 */
export function PayrollPeriodCard() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const query = usePayrollQuery({ week: getMondayOfWeek(todayIso()) });

  const employeeCount = query.data?.employeeCount ?? 0;
  const totalGrossPay = query.data?.totalGrossPay ?? 0;
  const weekStart = query.data?.weekStart ?? null;
  const weekEnd = query.data?.weekEnd ?? null;
  const locked = Boolean(query.data?.existingRun);

  /** A bare `YYYY-MM-DD` is a calendar day, not an instant — read it in UTC or it rolls back one. */
  const day = (date: string): string => {
    try {
      return formatUiDate(`${date}T00:00:00Z`, locale, {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      });
    } catch {
      return date;
    }
  };

  const period =
    weekStart && weekEnd
      ? t('home:payroll.period', { start: day(weekStart), end: day(weekEnd) })
      : undefined;

  return (
    <HomeCard
      headingId="home-payroll-heading"
      title={t('home:payroll.heading')}
      description={period}
      status={homeCardStatus(query, employeeCount)}
      errorText={t('home:payroll.unavailable')}
      emptyText={t('home:payroll.empty')}
      onRetry={() => void query.refetch()}
    >
      <p className="mt-3 font-display font-semibold text-3xl text-foreground tabular-nums">
        {formatNumber(totalGrossPay, locale, {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        })}
      </p>
      {/*
        WORDS, not a coloured pill. "Run locked" and "Not run yet" are the two states an Admin is
        actually deciding between, and a tinted chip beside every figure on Home would be one more
        label to learn rather than one more fact to read.
      */}
      <p className="mt-1 text-muted-foreground text-sm">
        {t('home:payroll.employees', { count: employeeCount })}
        {' · '}
        {t(locked ? 'home:payroll.locked' : 'home:payroll.notRun')}
      </p>
      {/*
        ANCHORED TO THE BOTTOM, not parked in the heading row beside the title like every other
        card's link. This card is one figure and one line, and in a row whose other cards run to a
        list of six it stretched to their height with the action stranded at the top and a void
        under it. `mt-auto` in the shell's flex column puts the way out where the card actually
        ends. The delta against the previous period would fill the gap honestly, but the endpoint
        returns no previous-period figure and deriving one here would be the client re-doing the
        gross-pay math (D-03) — so it waits for the server.
      */}
      <Link to="/payroll" search={{}} className={`mt-auto pt-4 ${HOME_CARD_LINK}`}>
        {t('home:payroll.open')}
      </Link>
    </HomeCard>
  );
}
